import { ThemeEvents } from '@theme/events';

const MOBILE_MAX_WIDTH = 749;
const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`;
const mobileMedia = window.matchMedia(MOBILE_MEDIA_QUERY);

/**
 * @typedef {Object} PlacementCache
 * @property {Element | null} parent
 * @property {Element | null} nextSibling
 */

/** @type {PlacementCache | null} */
let variantPlacementCache = null;

const selectVariantPicker = () => document.querySelector('.product-details variant-picker');

const selectPriceBlock = (container) =>
  container?.querySelector(':scope > product-price, :scope > .price-block, :scope > product-price-component') ?? null;

const ensurePlacementCache = () => {
  if (variantPlacementCache) return;
  const variantPicker = selectVariantPicker();
  if (!variantPicker) return;
  variantPlacementCache = {
    parent: variantPicker.parentElement,
    nextSibling: variantPicker.nextElementSibling,
  };
};

const restoreVariantPlacement = () => {
  const variantPicker = selectVariantPicker();
  if (!variantPicker || !variantPlacementCache?.parent) return;

  const { parent, nextSibling } = variantPlacementCache;
  if (!parent.contains(variantPicker)) return;

  if (nextSibling && parent.contains(nextSibling)) {
    parent.insertBefore(variantPicker, nextSibling);
  } else {
    parent.appendChild(variantPicker);
  }
};

const moveVariantBeforePrice = () => {
  if (!mobileMedia.matches) return;

  const variantPicker = selectVariantPicker();
  if (!variantPicker) return;

  const container = variantPicker.parentElement;
  if (!container) return;

  const priceBlock = selectPriceBlock(container);

  if (!priceBlock || priceBlock === variantPicker) return;
  if (variantPicker.nextElementSibling === priceBlock) return;

  container.insertBefore(variantPicker, priceBlock);
};

const getHeaderOffset = () => {
  const header = document.querySelector('#header-group');
  if (!header) return 0;
  const rect = header.getBoundingClientRect();
  return rect.height || 0;
};

const scrollGalleryIntoView = () => {
  if (!mobileMedia.matches) return;
  const media = document.querySelector('.product-information__media');
  if (!media) return;

  const headerOffset = getHeaderOffset();
  const rect = media.getBoundingClientRect();

  // Only scroll if the entire gallery is above the sticky header region.
  if (rect.bottom < headerOffset + 40) {
    const target = Math.max(rect.top + window.scrollY - headerOffset - 16, 0);
    window.scrollTo({ top: target, behavior: 'smooth' });
  }
};

const handleLayout = () => {
  ensurePlacementCache();
  if (mobileMedia.matches) {
    moveVariantBeforePrice();
  } else {
    restoreVariantPlacement();
  }
};

const handleVariantUpdate = () => {
  handleLayout();
  scrollGalleryIntoView();
  window.requestAnimationFrame(() => {
    document.querySelectorAll('[data-variant-inline-preview]').forEach((preview) => {
      preview.classList.remove('is-variant-animating');
      // force reflow so the animation can restart
      void preview.offsetWidth;
      preview.classList.add('is-variant-animating');
    });
  });
};

const init = () => {
  handleLayout();
  document.addEventListener(ThemeEvents.variantUpdate, handleVariantUpdate);
  mobileMedia.addEventListener('change', handleLayout);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
